import type Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton, type ButtonHandle } from "@/ui/Button";
import { createNativeTextInput, type NativeTextInputHandle } from "@/ui/NativeTextInput";
import { ShareChallengeController, type ShareChallengeModalState } from "@/daily/shareChallengeController";
import { buildShareDisclosure } from "@/daily/shareChallengeModalContent";
import { canUseNativeShare, performCopyLink, performCopyLinkNoTracking, performNativeShare, performNativeShareNoTracking } from "@/daily/shareFlow";
import type { ShareContext } from "@/analytics/events";

const PANEL_WIDTH = 460;

export interface ShareChallengeModalHandle {
  container: Phaser.GameObjects.Container;
  /** Opens the modal for a freshly created/retrieved challenge - does NOT invoke `navigator.share()` or the clipboard; those only ever happen from an explicit SHARE/COPY LINK tap inside the modal. */
  open(creatorDisplayName: string, targetDailyPoints: number, url: string): void;
  isOpen(): boolean;
  relayout(): void;
  destroy(): void;
}

/**
 * The branded in-game "CHALLENGE A FRIEND" modal (round-5 fix): shown
 * AFTER a challenge link is created/retrieved, BEFORE any OS share sheet
 * or clipboard write - discloses exactly what the round-5 spec requires
 * (the sharer's own public name + verified target) and offers SHARE
 * (native, only if supported) and COPY LINK as explicit, independent
 * choices, plus a CLOSE control. All of the actual state logic (busy,
 * feedback, cancellation-returns-to-modal, etc.) lives in the pure
 * `ShareChallengeController`; this file only renders whatever it reports.
 *
 * `options.qaMode` routes SHARE/COPY LINK through the no-tracking variants
 * of `shareFlow.ts` - the real browser Share/Clipboard APIs still run (so a
 * QA preview genuinely exercises them), but no `score_share_*` PostHog
 * event ever fires, matching `scoreChallengeQa.ts`'s "never contaminate
 * real funnel data" guarantee.
 */
export function createShareChallengeModal(
  scene: Phaser.Scene,
  depth: number,
  context: ShareContext,
  options: { qaMode?: boolean } = {},
): ShareChallengeModalHandle {
  const container = scene.add.container(0, 0).setDepth(depth).setVisible(false);

  const dim = scene.add.rectangle(0, 0, 10, 10, 0x0a0c12, 0.85);
  container.add(dim);

  const panelBg = scene.add.graphics();
  container.add(panelBg);

  const headlineText = scene.add
    .text(0, 0, "CHALLENGE A FRIEND", { fontFamily: "Arial, sans-serif", fontSize: "22px", fontStyle: "bold", color: "#f5f7fa" })
    .setOrigin(0.5, 0);
  container.add(headlineText);

  const disclosureText = scene.add
    .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "14px", color: "#d7dbe6", align: "center" })
    .setOrigin(0.5, 0);
  container.add(disclosureText);

  const feedbackText = scene.add
    .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "13px", fontStyle: "bold", align: "center" })
    .setOrigin(0.5, 0);
  container.add(feedbackText);

  const urlInput: NativeTextInputHandle = createNativeTextInput({ maxLength: 300, readOnly: true });
  urlInput.setVisible(false);

  const shareButton: ButtonHandle = createButton(scene, 0, 0, "SHARE", () => void controller.share(), { width: 200, height: 50 });
  container.add(shareButton);

  const copyButton: ButtonHandle = createButton(scene, 0, 0, "COPY LINK", () => void controller.copy(), {
    width: 200,
    height: 50,
    fillColor: 0x1c2030,
    hoverColor: 0x262c40,
    textColor: "#f5f7fa",
  });
  container.add(copyButton);

  const closeButton: ButtonHandle = createButton(scene, 0, 0, "CLOSE", () => controller.close(), {
    width: 160,
    height: 42,
    fillColor: 0x1c2030,
    hoverColor: 0x262c40,
    textColor: "#8b93a7",
  });
  container.add(closeButton);

  function onWindowKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") controller.close();
  }

  const nativeShareFn = options.qaMode
    ? (_ctx: ShareContext, dailyPoints: number, url: string) => performNativeShareNoTracking(dailyPoints, url)
    : performNativeShare;
  const copyLinkFn = options.qaMode ? (_ctx: ShareContext, url: string) => performCopyLinkNoTracking(url) : performCopyLink;

  const controller = new ShareChallengeController(context, {
    performNativeShare: nativeShareFn,
    performCopyLink: copyLinkFn,
    canUseNativeShare,
    onChange: render,
  });

  function feedbackColor(kind: string): string {
    switch (kind) {
      case "copied":
        return "#7dffb3";
      case "copy_failed":
        return "#ffd166";
      case "share_failed":
        return "#ff5f6d";
      case "cancelled":
        return "#8b93a7";
      default:
        return "#8b93a7";
    }
  }

  function feedbackMessage(state: Extract<ShareChallengeModalState, { phase: "open" }>): string {
    switch (state.feedback.kind) {
      case "copied":
        return "CHALLENGE LINK COPIED";
      case "copy_failed":
        return "Couldn't copy automatically — copy this link:";
      case "share_failed":
        return "Couldn't open the share sheet. Try COPY LINK instead.";
      case "cancelled":
        return "Share cancelled.";
      case "none":
        return "";
    }
  }

  function render(state: ShareChallengeModalState): void {
    if (state.phase !== "open") {
      container.setVisible(false);
      urlInput.setVisible(false);
      globalThis.window?.removeEventListener?.("keydown", onWindowKeyDown);
      return;
    }
    container.setVisible(true);
    globalThis.window?.addEventListener?.("keydown", onWindowKeyDown);

    const { width, height } = scene.scale;
    const panelWidth = Math.min(PANEL_WIDTH, width * 0.92);
    const wrapWidth = panelWidth - 56;

    disclosureText.setText(buildShareDisclosure(state.creatorDisplayName, state.targetDailyPoints));
    disclosureText.setWordWrapWidth(wrapWidth, true);

    const showFeedback = state.feedback.kind !== "none";
    feedbackText.setText(feedbackMessage(state));
    feedbackText.setColor(feedbackColor(state.feedback.kind));
    feedbackText.setWordWrapWidth(wrapWidth, true);
    feedbackText.setVisible(showFeedback);

    const showUrlFallback = state.feedback.kind === "copy_failed";
    if (showUrlFallback) urlInput.setValue(state.url);

    shareButton.setVisible(state.canNativeShare);
    shareButton.setEnabled(state.canNativeShare && !state.busy);
    copyButton.setEnabled(!state.busy);

    // Padding + accumulated content height (`y`) fully drives both where
    // each element lands AND the panel's own size - never a fixed panel
    // height that could clip a long disclosure/feedback line, matching
    // this codebase's established dynamic-layout convention (see e.g.
    // `DailyResultScene.buildLayout()`).
    const padding = 26;
    let y = padding;

    headlineText.setPosition(0, y);
    y += headlineText.height + 14;

    disclosureText.setPosition(0, y);
    y += disclosureText.height + 20;

    // Always positioned (never left at a stale local coordinate from a
    // previous render), even when hidden - `render()` re-shifts every
    // element from local (0, y) to absolute panel coordinates on EVERY
    // call, so an element skipped here would otherwise accumulate the
    // panel-origin shift a second time on the next re-render.
    feedbackText.setPosition(0, y);
    if (showFeedback) y += feedbackText.height + (showUrlFallback ? 10 : 18);

    const inputHeight = 42;
    let inputTopLocal = 0;
    if (showUrlFallback) {
      inputTopLocal = y;
      y += inputHeight + 20;
    }

    const buttonGap = 14;
    // Same "always position, even when hidden" rule as `feedbackText`
    // above - `shareButton` stays parented in `container` regardless of
    // `canNativeShare`, so a stale local position would otherwise double-
    // shift on the next render.
    shareButton.setPosition(0, y + 25);
    if (state.canNativeShare) y += 50 + buttonGap;
    copyButton.setPosition(0, y + 25);
    y += 50 + buttonGap;
    closeButton.setPosition(0, y + 21);
    y += 42 + padding;

    const panelHeight = Math.min(y, height * 0.92);
    const panelX = width / 2 - panelWidth / 2;
    const panelY = height / 2 - panelHeight / 2;

    dim.setSize(width, height).setPosition(width / 2, height / 2);

    panelBg.clear();
    panelBg.fillStyle(0x171a24, 1);
    panelBg.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);
    panelBg.lineStyle(1.5, GameConfig.colors.gridLine, 1);
    panelBg.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);

    // Every other element above was positioned relative to (0, padding..)
    // as if the panel's top-left were the origin - shifting the whole
    // group by (panelX + panelWidth/2, panelY) in one place here keeps
    // every individual `setPosition()` call above simple and panel-size-
    // independent.
    const originX = panelX + panelWidth / 2;
    for (const obj of [headlineText, disclosureText, feedbackText, shareButton, copyButton, closeButton]) {
      obj.x += originX;
      obj.y += panelY;
    }

    if (showUrlFallback) {
      urlInput.layout(scene, panelX + 28, panelY + inputTopLocal, panelWidth - 56, inputHeight);
      urlInput.setVisible(true);
      urlInput.selectAll();
    } else {
      urlInput.setVisible(false);
    }
  }

  return {
    container,
    open(creatorDisplayName, targetDailyPoints, url) {
      controller.open(creatorDisplayName, targetDailyPoints, url);
    },
    isOpen: () => controller.isOpen(),
    relayout() {
      render(controller.getState());
    },
    destroy() {
      controller.close();
      globalThis.window?.removeEventListener?.("keydown", onWindowKeyDown);
      urlInput.destroy();
      container.destroy();
    },
  };
}
